/**
 * Stato di una lista del BO (paginazione, ordinamento, ricerca, filtri) tenuto nell'URL.
 *
 * Con un refresh, o aprendo un link, la lista riparte esattamente da dove era: stessa
 * pagina, stesse righe per pagina, stessi filtri. L'URL si aggiorna con `replace`,
 * quindi la cronologia non si riempie di una voce per ogni cambio pagina.
 *
 * Uso nella pagina:
 *
 *   mixins: [listRouteState({ filters: { supplier: 'string', created_at: 'array' }, perPageOptions: [15, 25, 50] })],
 *   data() {
 *     return { settings: this.listSettingsFromRoute({ search: '', filters: {...}, options: {...} }) };
 *   },
 *
 * La pagina deve avere `settings` ({ search, filters, options }) e il metodo di caricamento
 * (`getDataFromApi` se non indicato diversamente); `searchfield`, se c'e', viene allineato
 * alla ricerca letta dall'URL. La pagina NON deve avere un suo watcher su `settings`:
 * caricamento, ritorno a pagina 1 al cambio filtri e aggiornamento dell'URL li fa il mixin.
 */
import _ from 'lodash';
import { rememberListRoute } from './listReturn';

// Nomi riservati nella query: i filtri non devono usarli come chiave.
const PAGE = 'page';
const PER_PAGE = 'perPage';
const SORT = 'sort';
const DESC = 'desc';
const SEARCH = 'q';

const isEmpty = value => value === '' || value === null || value === undefined || (Array.isArray(value) && value.length === 0);

function readFilter(type, raw) {
	if (raw === undefined || raw === null) return undefined;
	const value = Array.isArray(raw) ? raw.join(',') : String(raw);
	switch (type) {
		case 'array':
			return value === '' ? [] : value.split(',');
		// id numerici (es. categorie): il backend li confronta come numeri, non come stringhe
		case 'numberArray':
			return value === '' ? [] : value.split(',').map(Number).filter(Number.isFinite);
		// scelte si'/no multiple (es. Spedito: [true, false])
		case 'booleanArray':
			return value === '' ? [] : value.split(',').map(v => v === '1');
		case 'number': {
			const number = Number(value);
			return Number.isFinite(number) ? number : undefined;
		}
		case 'boolean':
			return value === '1' || value === 'true';
		default:
			return value;
	}
}

function writeFilter(type, value) {
	switch (type) {
		case 'array':
		case 'numberArray':
			return value.join(',');
		case 'booleanArray':
			return value.map(v => (v ? '1' : '0')).join(',');
		case 'boolean':
			return value ? '1' : '0';
		default:
			return String(value);
	}
}

/** Lo stato di `settings` che conta per URL e caricamento, come query string (valori di default omessi). */
export function settingsToQuery(settings, defaults, schema) {
	const query = {};
	const { page, itemsPerPage, sortBy, sortDesc } = settings.options;

	if (page && page !== 1) query[PAGE] = String(page);
	if (itemsPerPage && itemsPerPage !== defaults.options.itemsPerPage) query[PER_PAGE] = String(itemsPerPage);

	const sort = sortBy && sortBy[0];
	const desc = !!(sortDesc && sortDesc[0]);
	if (sort && (sort !== defaults.options.sortBy[0] || desc !== !!defaults.options.sortDesc[0])) {
		query[SORT] = sort;
		query[DESC] = desc ? '1' : '0';
	}

	if (!isEmpty(settings.search)) query[SEARCH] = settings.search;

	Object.keys(schema).forEach(key => {
		const value = settings.filters[key];
		if (_.isEqual(value, defaults.filters[key])) return;
		if (!isEmpty(value)) {
			query[key] = writeFilter(schema[key], value);
		} else if (!isEmpty(defaults.filters[key])) {
			// svuotato dall'utente un filtro che di default ha un valore (es. date dell'ultimo mese):
			// va scritto vuoto, altrimenti al refresh tornerebbe il default
			query[key] = '';
		}
	});

	return query;
}

/** `settings` ricostruiti dalla query, partendo dai default; i valori non validi tornano al default. */
export function settingsFromQuery(query, defaults, schema, perPageOptions) {
	const settings = _.cloneDeep(defaults);
	const options = settings.options;

	const page = parseInt(query[PAGE], 10);
	if (page > 0) options.page = page;

	const perPage = parseInt(query[PER_PAGE], 10);
	if (perPage > 0 && (!perPageOptions || perPageOptions.includes(perPage))) options.itemsPerPage = perPage;

	if (query[SORT]) {
		options.sortBy = [String(query[SORT])];
		options.sortDesc = [query[DESC] === '1'];
	}

	if (query[SEARCH] !== undefined) settings.search = String(query[SEARCH]);

	Object.keys(schema).forEach(key => {
		const value = readFilter(schema[key], query[key]);
		if (value !== undefined) settings.filters[key] = value;
	});

	return settings;
}

/** Confronto fra query di vue-router: i valori arrivano come stringhe, a volte come array. */
function sameQuery(a, b) {
	const keys = Object.keys(a);
	if (keys.length !== Object.keys(b).length) return false;
	return keys.every(key => String(a[key]) === String(b[key]));
}

export function listRouteState({ filters = {}, perPageOptions = null, fetch = 'getDataFromApi' } = {}) {
	return {
		created() {
			if ('searchfield' in this.$data) this.searchfield = this.settings.search;
			// il primo caricamento lo fa `listLoad()`: la tabella al montaggio riscrive `options`
			// senza cambiare niente, e non deve far partire un secondo caricamento
			this.listLastKey = JSON.stringify(this.listQuery());
			this.listLastFiltersKey = this.listFiltersKey();
			this.listSyncRoute();
		},

		watch: {
			settings: {
				deep: true,
				handler() {
					this.listOnSettingsChange();
				},
			},
			// Navigazione esterna sulla stessa lista (es. voce di menu senza parametri):
			// lo stato si riallinea all'URL nuovo.
			'$route.query'(query) {
				// il token del ritorno (from) non fa parte dello stato della lista
				// eslint-disable-next-line no-unused-vars
				const { from, ...stato } = query;
				if (sameQuery(stato, this.listQuery())) return;
				this.listApplyingRoute = true;
				this.settings = settingsFromQuery(query, this.listDefaults, filters, perPageOptions);
				if ('searchfield' in this.$data) this.searchfield = this.settings.search;
				this.listLastFiltersKey = this.listFiltersKey();
				this.$nextTick(() => {
					this.listApplyingRoute = false;
				});
			},
		},

		methods: {
			/** Da chiamare in `data()`: i default della pagina, sovrascritti da quello che c'e' nell'URL. */
			listSettingsFromRoute(defaults) {
				this.listDefaults = _.cloneDeep(defaults);
				return settingsFromQuery(this.$route.query, this.listDefaults, filters, perPageOptions);
			},

			listQuery() {
				return settingsToQuery(this.settings, this.listDefaults, filters);
			},

			listFiltersKey() {
				return JSON.stringify([this.settings.search, _.pick(this.settings.filters, Object.keys(filters))]);
			},

			listSyncRoute() {
				const query = this.listQuery();
				// lista aperta da un'altra pagina (openWithReturn): il token del ritorno resta nell'URL,
				// ma non nello stato ricordato, che vale anche per le aperture normali
				const conRitorno = this.$route.query.from ? { ...query, from: String(this.$route.query.from) } : query;
				if (!sameQuery(conRitorno, this.$route.query)) {
					this.$router.replace({ query: conRitorno }).catch(() => {});
				}
				rememberListRoute(this.$route.path, query);
			},

			listOnSettingsChange() {
				// Cambiano ricerca o filtri: si riparte dalla prima pagina. Il watcher
				// riscatta col page aggiornato e solo allora si carica.
				const filtersKey = this.listFiltersKey();
				if (filtersKey !== this.listLastFiltersKey) {
					this.listLastFiltersKey = filtersKey;
					if (!this.listApplyingRoute && this.settings.options.page !== 1) {
						this.settings.options.page = 1;
						return;
					}
				}

				this.listSyncRoute();

				// v-data-table riscrive `options` anche senza cambiamenti veri (groupBy,
				// multiSort...): si ricarica solo se cambia qualcosa che finisce nella query.
				const key = JSON.stringify(this.listQuery());
				if (key === this.listLastKey) return;
				this.listLastKey = key;
				this[fetch]();
			},

			/** Da chiamare al primo caricamento della pagina, al posto della chiamata diretta. */
			listLoad() {
				this.listLastKey = JSON.stringify(this.listQuery());
				return this[fetch]();
			},
		},
	};
}
